import React, { Component } from 'react';
import { withFormik, Field } from 'formik';
import { Form, Button } from 'semantic-ui-react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withLocalize } from 'react-localize-redux';
import axios from 'axios';
import { pick, has } from 'lodash';
import { withRouter, Redirect } from 'react-router-dom';

import { HHAlert, HHEncrypt } from '../common/util';
import { ALERT_TYPE } from '../common/constant';
import { xinshiSignupFormValidator } from './validators';
import { HHInput, HHSingleSelect } from './fields';
import { HHSERVICES_PHONE_GET_VERIFYCODE, HHSERVICES_PHONE_VERIFY, DJANGO_XINSHIBEI_REGISTER } from '../network/api';
import { xinshiUserSignup } from '../actions/xinshi';
import { signInQRCode } from '../actions';

const formikEnhancer = withFormik({
  validationSchema: xinshiSignupFormValidator,
  mapPropsToValues: ({ user }) => ({
    email: (has(user, 'profile.basicInfo.email'))
      ? user.profile.basicInfo.email
      : '',
    countryDialCode: 86,
    number: '',
    verificationCode: '',
    password: '',
    confirmPassword: ''
  }),
  handleSubmit: async (values, { setSubmitting, props }) => {
    const verifiedValues = await xinshiSignupFormValidator.validate(values);
    verifiedValues.password = HHEncrypt(verifiedValues.password);

    const submittedValues = pick(verifiedValues, [
      'email',
      'password',
      'countryDialCode',
      'number'
    ]);

    axios.post(
      DJANGO_XINSHIBEI_REGISTER,
      submittedValues
    ).then((response) => {
      const { existing, code, token } = response.data;
      if (code === 200) {
        if (existing) {
          HHAlert({ id: 'alert.warning.existingAccountUseOldPass' }, ALERT_TYPE.WARNING);
        }
        props.xinshiUserSignup(response.data);

        props.signInQRCode(token);

        axios.post(HHSERVICES_PHONE_VERIFY, {
          number: verifiedValues.number,
          countryDialCode: verifiedValues.countryDialCode,
          code: verifiedValues.verificationCode
        }).then(({ data }) => {
          if (data.code === 200) {
            props.history.push(`${props.match.url.replace(/\/$/, '')}/events`);
          } else {
            HHAlert({ id: 'alert.error.phoneVerificationFail' }, ALERT_TYPE.ERROR);
          }
        });
      }
    }).catch(() => {
      setSubmitting(false);
    });
  }
});

class XinshiSignupForm extends Component {
  state = {
    disableVerifyButton: false,
    verificationCodePhoneCombinations: {}
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  getVerificationCode = () => {
    const { activeLanguage, values } = this.props;
    const { number, countryDialCode } = values;
    this.setState({ disableVerifyButton: true });
    axios.post(
      HHSERVICES_PHONE_GET_VERIFYCODE,
      { number, countryDialCode, language: activeLanguage.code.toLowerCase() }
    ).then(({ data }) => {
      switch (data.code) {
        case 200: {
          this.setState((prevState) => ({
            verificationCodePhoneCombinations: {
              ...prevState.verificationCodePhoneCombinations,
              [number]: data.verifyCode
            }
          }));
          this.timeout = setTimeout(() => { this.setState({ disableVerifyButton: false }); }, 1000 * 60);
          break;
        }
        case 4004: {
          this.setState({ disableVerifyButton: false });
          HHAlert({ id: 'alert.error.numberAlreadyExisted' }, ALERT_TYPE.ERROR);
          break;
        }
        default:
          HHAlert({ id: 'alert.error.unknownError' }, ALERT_TYPE.ERROR);
      }
    }).catch(() => {
      HHAlert({ id: 'alert.error.unknownError' }, ALERT_TYPE.ERROR);
    });
  }

  isVerificationCodeValid = () => {
    const { number, verificationCode } = this.props.values;
    if (parseInt(verificationCode, 10) !== this.state.verificationCodePhoneCombinations[number]) {
      return false;
    }
    return true;
  }

  render() {
    const {
      translate, handleSubmit, isSubmitting, values, errors, user, match
    } = this.props;

    let isPhoneVerified = false;

    if (has(user, 'profile.basicInfo.isPhoneVerified')) {
      // eslint-disable-next-line
      isPhoneVerified = user.profile.basicInfo.isPhoneVerified;
    }

    if (isPhoneVerified) {
      return <Redirect to={`${match.url}/events`} />;
    }

    const dialCodeOptions = [
      { label: translate('common.usaCanda1'), value: 1 },
      { label: translate('common.singapore65'), value: 65 },
      { label: translate('common.japan81'), value: 81 },
      { label: translate('common.korea82'), value: 82 },
      { label: translate('common.hongkong852'), value: 852 },
      { label: translate('common.macou853'), value: 853 },
      { label: translate('common.china86'), value: 86 },
      { label: translate('common.taiwan886'), value: 886 }
    ];

    const { disableVerifyButton } = this.state;

    const renderVerifyButton = () => (
      <Button
        type='button'
        content={translate('button.getVerificationCode')}
        disabled={disableVerifyButton || !values.number || !!errors.number}
        onClick={() => { this.getVerificationCode(); }}
      />
    );

    return (
      <Form onSubmit={(vals, formikProps) => {
        if (this.isVerificationCodeValid()) {
          handleSubmit(vals, formikProps);
        } else {
          HHAlert({ id: 'alert.error.invalidPhoneVerifyCode' }, ALERT_TYPE.ERROR);
        }
      }}
      >
        <Form.Field>
          <label htmlFor='email'>{translate('form.label.email')}</label>
          <Field
            name='email'
            component={HHInput}
            otherProps={{
              id: 'email',
              placeholder: translate('form.placeholder.email'),
              type: 'email'
            }}
          />
        </Form.Field>

        <Form.Group>
          <Form.Field inline width={4}>
            <label htmlFor='countryDialCode'>
              {translate('form.label.countryDialCode')}
            </label>
            <Field
              name='countryDialCode'
              component={HHSingleSelect}
              otherProps={{
                id: 'countryDialCode',
                options: dialCodeOptions,
                simpleValue: true,
                searchable: false,
                clearable: false
              }}
            />
          </Form.Field>

          <Form.Field inline width={12}>
            <label htmlFor='number'>
              {translate('form.label.phoneNumber')}
            </label>
            <Field
              label='Number'
              name='number'
              component={HHInput}
              otherProps={{
                id: 'number',
                placeholder: translate('form.placeholder.phoneNumber')
              }}
            />
          </Form.Field>
        </Form.Group>

        <label>
          {translate('form.label.verifyCode')}
        </label>
        <Form.Field>
          <Field
            name='verificationCode'
            component={HHInput}
            otherProps={{
              id: 'verificationCode',
              placeholder: translate('form.placeholder.verifyCode'),
              action: renderVerifyButton()
            }}
          />
        </Form.Field>

        <Form.Field>
          <label htmlFor='newPassword'>
            {translate('form.label.password')}
          </label>
          <Field
            name='password'
            component={HHInput}
            otherProps={{
              id: 'password',
              placeholder: translate('form.placeholder.password'),
              type: 'password'
            }}
          />
          <div style={{ color: 'grey', zoom: 0.8 }}>
            {translate('form.reminder.password')}
          </div>
        </Form.Field>
        <Form.Field>
          <label htmlFor='confirmPassword'>
            {translate('form.label.confirmPassword')}
          </label>
          <Field
            name='confirmPassword'
            component={HHInput}
            otherProps={{
              id: 'confirmPassword',
              placeholder: translate('form.placeholder.confirmPassword'),
              type: 'password'
            }}
          />
          <div style={{ color: 'grey', zoom: 0.8 }}>
            {translate('form.reminder.password')}
          </div>
        </Form.Field>
        <Button
          primary
          fluid
          disabled={isSubmitting}
          loading={isSubmitting}
          type='submit'
          size='big'
        >
          {translate('button.submit')}
        </Button>
      </Form>
    );
  }
}

export default compose(
  withLocalize,
  withRouter,
  connect(({ user }) => ({ user }), { xinshiUserSignup, signInQRCode }),
  formikEnhancer,
)(XinshiSignupForm);




// WEBPACK FOOTER //
// ./src/components/forms/xinshiSignupForm.js